import { DateTimeStr } from '@mi-front/core';
import { MRT_ColumnDef, MRT_RowData } from 'material-react-table';
import { defineMessage, IntlFormatters, MessageDescriptor } from 'react-intl';
import { LDateTime } from './LDateTime';

/** Shared table column header for date + time columns. */
export const dateTimeColumnHeaderMessage = defineMessage({
  id: 'common.datetime',
  defaultMessage: 'Fecha y hora',
});

export interface CreateDateTimeColumnOptions<T extends MRT_RowData> {
  /** Reads the ISO/datetime string from the row. Empty values render an empty cell. */
  accessor: (row: T) => string | undefined | null;
  /** Column id used for sorting and filtering. Defaults to `datetime`. */
  id?: string;
  /**
   * IANA timezone, or a per-row getter.
   * When omitted, `LDateTime` uses timezone context or the browser default.
   */
  tz?: string | ((row: T) => string | undefined);
  /** Override the default "Fecha y hora" header. */
  header?: string | MessageDescriptor;
  showSeconds?: boolean;
  /** Relative time below the date. Defaults to `true`. */
  showRelative?: boolean | 'inline';
  size?: number;
  enableSorting?: boolean;
  enableColumnFilter?: boolean;
  filterVariant?: MRT_ColumnDef<T>['filterVariant'];
}

/**
 * Builds a reusable MRT column: DMY date, time, and relative time below.
 * Header defaults to `common.datetime` ("Fecha y hora").
 */
export function createDateTimeColumn<T extends MRT_RowData>(
  $t: IntlFormatters['$t'],
  options: CreateDateTimeColumnOptions<T>,
): MRT_ColumnDef<T> {
  const {
    accessor,
    id = 'datetime',
    tz,
    header,
    showSeconds = false,
    showRelative = true,
    size = 160,
    enableSorting = true,
    enableColumnFilter,
    filterVariant,
  } = options;

  const resolvedHeader =
    header == null
      ? $t(dateTimeColumnHeaderMessage)
      : typeof header === 'string'
        ? header
        : $t(header);

  return {
    id,
    header: resolvedHeader,
    size,
    enableSorting,
    ...(enableColumnFilter !== undefined ? { enableColumnFilter } : {}),
    ...(filterVariant !== undefined ? { filterVariant } : {}),
    accessorFn: (row) => {
      const value = accessor(row);
      return value ? new Date(value) : null;
    },
    Cell: ({ row }) => {
      const value = accessor(row.original);
      if (!value) {
        return null;
      }

      const timezone = typeof tz === 'function' ? tz(row.original) : tz;

      return (
        <LDateTime
          value={value as DateTimeStr}
          showRelative={showRelative}
          showSeconds={showSeconds}
          tz={timezone}
        />
      );
    },
  };
}
